/**
 * Utility functions for gauge color calculations based on KPI performance
 */

/**
 * Performance color constants
 */
export const GAUGE_COLORS = {
  INVALID: '#E0E0E0', // Gray - Invalid targets (target = 0)
  POOR: '#E53935', // Red - Poor performance (< 40%)
  AVERAGE: '#F5A623', // Orange - Average performance (40-70%)
  GOOD: '#4CAF50', // Green - Good performance (> 70%)
} as const;

/**
 * Performance thresholds for KPI evaluation
 */
export const PERFORMANCE_THRESHOLDS = {
  POOR_THRESHOLD: 40, // Below this percentage = poor performance
  AVERAGE_THRESHOLD: 70, // Below this percentage = average performance
} as const;

/**
 * Determines gauge color based on current performance vs target
 *
 * Color coding:
 * - Gray: Invalid targets (target = 0)
 * - Red: Poor performance (< 40%)
 * - Orange: Average performance (40-70%)
 * - Green: Good performance (> 70%)
 *
 * @param current - Current KPI value
 * @param target - Target KPI value
 * @returns Color hex string based on performance percentage
 *
 * @example
 * getGaugeColor(30, 100) // returns '#E53935' (Red - 30%)
 * getGaugeColor(60, 100) // returns '#F5A623' (Orange - 60%)
 * getGaugeColor(80, 100) // returns '#4CAF50' (Green - 80%)
 * getGaugeColor(50, 0)   // returns '#E0E0E0' (Gray - invalid target)
 */
export const getGaugeColor = (current: number, target: number): string => {
  if (target === 0) return GAUGE_COLORS.INVALID;

  const percentage = (current / target) * 100;

  if (percentage < PERFORMANCE_THRESHOLDS.POOR_THRESHOLD) return GAUGE_COLORS.POOR;
  if (percentage < PERFORMANCE_THRESHOLDS.AVERAGE_THRESHOLD) return GAUGE_COLORS.AVERAGE;
  return GAUGE_COLORS.GOOD;
};
